import React, { useEffect, useState, useRef } from 'react';

/**
 * Floating "🔴 47 reading now" pill for article pages.
 *
 * How it works:
 *   1. On mount, generate/reuse a stable visitor_id in localStorage.
 *   2. Beacon `POST /api/articles/{slug}/heartbeat` every 25s while the page
 *      is visible (paused when tab is hidden - no phantom readers).
 *   3. Poll `GET /api/articles/{slug}/live-count` every 15s.
 *   4. Render the pill only when count >= 2. Under that it just looks lonely
 *      and inflates the perceived quiet on new pieces.
 *
 * Pure-editorial styling: solid red dot, matte-grey chip, tiny type.
 * No animation beyond a subtle pulse on the dot.
 */

const API = process.env.REACT_APP_BACKEND_URL;
const HEARTBEAT_MS = 25_000;
const POLL_MS = 15_000;
const VISITOR_KEY = 'outlaw_visitor_id_v1';
const SHOW_THRESHOLD = 2;

function getVisitorId() {
  try {
    let vid = localStorage.getItem(VISITOR_KEY);
    if (!vid) {
      // Small, URL-safe, no libs needed.
      vid = 'v_' + Math.random().toString(36).slice(2, 10) +
            Date.now().toString(36).slice(-6);
      localStorage.setItem(VISITOR_KEY, vid);
    }
    return vid;
  } catch {
    // Private-mode Safari can throw - fall back to a session id.
    return 'v_anon_' + Math.random().toString(36).slice(2, 12);
  }
}

export default function LiveReaderPill({ slug }) {
  const [count, setCount] = useState(0);
  const visitorRef = useRef(null);

  useEffect(() => {
    if (!slug || !API) return undefined;
    visitorRef.current = getVisitorId();

    let cancelled = false;

    const sendHeartbeat = async () => {
      if (document.hidden || cancelled) return;
      try {
        const res = await fetch(`${API}/api/articles/${slug}/heartbeat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ visitor_id: visitorRef.current }),
        });
        if (res.ok) {
          const data = await res.json();
          if (!cancelled && typeof data.live === 'number') setCount(data.live);
        }
      } catch {
        /* silent - pill just won't update */
      }
    };

    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await fetch(`${API}/api/articles/${slug}/live-count`);
        if (res.ok) {
          const data = await res.json();
          if (!cancelled && typeof data.live === 'number') setCount(data.live);
        }
      } catch {
        /* silent */
      }
    };

    // Immediate heartbeat so we're counted right away, then start intervals.
    sendHeartbeat();
    const hbInterval = setInterval(sendHeartbeat, HEARTBEAT_MS);
    const pollInterval = setInterval(poll, POLL_MS);

    // When the tab becomes visible again, re-beacon + re-poll immediately.
    const onVisibility = () => {
      if (!document.hidden) {
        sendHeartbeat();
        poll();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      clearInterval(hbInterval);
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [slug]);

  if (count < SHOW_THRESHOLD) return null;

  return (
    <div
      className="fixed bottom-24 left-4 z-40 flex items-center gap-2 rounded-full border border-zinc-700 bg-[#111113]/95 px-3 py-1.5 text-xs font-semibold text-white shadow-lg backdrop-blur-sm sm:bottom-6"
      data-testid="live-reader-pill"
      title={`${count} people reading this article right now`}
    >
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-70" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
      </span>
      <span data-testid="live-reader-count">{count}</span>
      <span className="text-zinc-400 font-normal">reading now</span>
    </div>
  );
}
